import dicomParser from 'dicom-parser';
import { hasDicmMagic, isDicomDir } from './sniff';
import { readDecodedString } from './tagBrowser';
export const NON_DICOM_GROUP_KEY = '__non_dicom__';
export const UNPARSEABLE_GROUP_KEY = '__unparseable__';
const DICOM_EXTENSIONS = new Set(['.dcm']);
const SUPPORTED_IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png']);
// Leading slices tried, smallest first, until one holds the whole header. 64 KB covers an
// ordinary image; a whole-slide image or a multi-frame object can carry hundreds of KB of
// sequences (optical paths, per-frame functional groups, an ICC profile) before its pixels.
const HEADER_SLICE_STEPS = [64 * 1024, 1024 * 1024, 16 * 1024 * 1024];
// Pixel Data (7FE0,0010). Everything an import reads sits before it, so parsing stops there.
const PIXEL_DATA_TAG = 'x7fe00010';
const PARSE_CONCURRENCY = 10;
function fileExtension(name) {
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}
async function classifyFile(file) {
    const ext = fileExtension(file.name);
    if (DICOM_EXTENSIONS.has(ext))
        return 'dicom';
    if (SUPPORTED_IMAGE_EXTENSIONS.has(ext))
        return 'image';
    if (file.type === 'application/dicom')
        return 'dicom';
    if (file.type.startsWith('image/jpeg') || file.type.startsWith('image/png'))
        return 'image';
    // A modality CD names its images IM000001 or after their UIDs, with no .dcm
    // and no MIME type, so the header decides. DICOMDIR, the CD's index, is a
    // Part 10 file too but holds no image: it stays out, as it always has.
    if (isDicomDir(file))
        return 'unsupported';
    return (await hasDicmMagic(file)) ? 'dicom' : 'unsupported';
}
async function readSliceAsUint8(file, end) {
    const blob = file.slice(0, end);
    const buf = await blob.arrayBuffer();
    return new Uint8Array(buf);
}
async function readFullAsUint8(file) {
    const buf = await file.arrayBuffer();
    return new Uint8Array(buf);
}
/**
 * Parses a DICOM file's header — every element before Pixel Data — from the smallest leading
 * slice that holds all of it, without reading the pixels.
 *
 * It used to parse a 64 KB slice with no stopping point, which always ran into Pixel Data, threw
 * "buffer overrun", and fell back to reading the WHOLE file into memory: 690 MB in the tab for one
 * level of a whole-slide image, before its upload had even started. Stopping at (7FE0,0010) makes
 * the slice enough for any ordinary header. A slice's parse is trusted only when it reached Pixel
 * Data or was the whole file; anything else (a cut that happened to land on an element boundary)
 * tries the next size. Only a genuinely odd file — a header past 16 MB, or no Pixel Data and over
 * 16 MB — still reads the file whole.
 */
async function parseDicomHeader(file) {
    for (const step of HEADER_SLICE_STEPS) {
        const end = Math.min(step, file.size);
        const wholeFile = end >= file.size;
        try {
            const ds = dicomParser.parseDicom(await readSliceAsUint8(file, end), {
                untilTag: PIXEL_DATA_TAG,
            });
            if (wholeFile || ds.elements[PIXEL_DATA_TAG])
                return ds;
        }
        catch (error) {
            // The header runs past this slice — try a larger one. Once the slice was the whole
            // file, a larger read cannot help.
            if (wholeFile)
                throw error;
        }
        if (wholeFile)
            break;
    }
    return dicomParser.parseDicom(await readFullAsUint8(file), { untilTag: PIXEL_DATA_TAG });
}
async function extractStudyUid(file) {
    try {
        return (await parseDicomHeader(file)).string('x0020000d') ?? null;
    }
    catch {
        return null;
    }
}
/**
 * Extracts StudyInstanceUID and SOPInstanceUID from a DICOM file for use in
 * the direct-GCS import engine's pre-flight dedup call. Non-DICOM files return
 * all-null with isDicom=false immediately; unparseable DICOM files return
 * null UIDs with isDicom=true so the caller can still route the file.
 *
 * Reads the header only (see parseDicomHeader), never the pixels.
 */
export async function extractImportTags(file) {
    if ((await classifyFile(file)) !== 'dicom')
        return { studyUid: null, sopUid: null, isDicom: false };
    try {
        const ds = await parseDicomHeader(file);
        return {
            studyUid: ds.string('x0020000d') ?? null,
            sopUid: ds.string('x00080018') ?? null,
            isDicom: true,
        };
    }
    catch {
        // DICOM by classification but headers are unreadable.
        return { studyUid: null, sopUid: null, isDicom: true };
    }
}
/** Format a DICOM PN value ("Family^Given^Middle^...") for display. */
function formatPersonName(raw) {
    if (!raw)
        return null;
    const parts = raw.split('^').map((p) => p.trim());
    const family = parts[0] ?? '';
    const given = parts[1] ?? '';
    const formatted = [family, given].filter(Boolean).join(', ');
    return formatted || raw.trim() || null;
}
/**
 * Best-effort extraction of human-friendly study tags (patient / description /
 * modality) from a single DICOM file, for the "recent imports" list. Never
 * throws — returns all-null when the file isn't parseable DICOM.
 */
export async function extractStudyDisplayTags(file) {
    if ((await classifyFile(file)) !== 'dicom') {
        return { patientName: null, description: null, modality: null };
    }
    try {
        const ds = await parseDicomHeader(file);
        return {
            // Patient name (PN) and study description (LO) are encoded per Specific
            // Character Set, and `ds.string` reads one byte per character whatever the
            // file declares — a UTF-8 study's "Müller" would show as "MÃ¼ller" in the
            // recent-imports list. Modality (0008,0060) is CS, so ASCII by definition.
            patientName: formatPersonName(readDecodedString(ds, 'x00100010')),
            description: readDecodedString(ds, 'x00081030') || null,
            modality: ds.string('x00080060')?.trim() || null,
        };
    }
    catch {
        return { patientName: null, description: null, modality: null };
    }
}
/**
 * Groups input files by DICOM StudyInstanceUid for the import endpoint's
 * single-study-per-request contract. Non-DICOM files are folded into one
 * synthetic group; files whose headers cannot be parsed go into another
 * synthetic group so the server can still report them as per-file errors.
 *
 * Order preservation: groups appear in first-seen order; within each group
 * files retain their original index order.
 */
export async function groupFilesByStudy(files) {
    const groupOrder = [];
    const groupsByKey = new Map();
    function append(key, file) {
        if (!groupsByKey.has(key)) {
            groupsByKey.set(key, []);
            groupOrder.push(key);
        }
        groupsByKey.get(key).push(file);
    }
    const resolved = new Array(files.length);
    for (let i = 0; i < files.length; i += PARSE_CONCURRENCY) {
        const chunk = files.slice(i, i + PARSE_CONCURRENCY);
        const chunkResults = await Promise.all(chunk.map(async (file) => {
            const kind = await classifyFile(file);
            if (kind === 'image' || kind === 'unsupported') {
                return { file, key: NON_DICOM_GROUP_KEY };
            }
            const uid = await extractStudyUid(file);
            return { file, key: uid ?? UNPARSEABLE_GROUP_KEY };
        }));
        chunkResults.forEach((r, j) => {
            resolved[i + j] = r;
        });
        await new Promise((r) => setTimeout(r, 0));
    }
    for (const r of resolved)
        append(r.key, r.file);
    return groupOrder.map((key) => ({ key, files: groupsByKey.get(key) }));
}
